import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

// Services
import { AuthService } from '@app/auth/auth.service';

// Models
import { ParsedGraphQLError } from '@app/models/utils/graphql-error.model';
import { ErrorContextInfo } from '@app/models/utils/sentry-error.model';
import { Operation } from '@apollo/client/link/core/types';
import { ApolloError } from '@apollo/client/errors';
import { GraphQLError } from 'graphql/error/GraphQLError';

import * as Sentry from '@sentry/angular-ivy';

@Injectable({
  providedIn: 'root',
})
export class GraphQLErrorUtilsService {
  // #region -> Property - Defined rewrite message pairs <-
  // ----------

  KNOWN_REWRITE_MESSAGE_PATHS = {
    '500-createIFrameHash-Internal Server Error':
      'Target Franchise does not have correct TranSafe credentials.',
    '404-getPet-Not Found': 'Pet ID not found.',
    '403-getPet-Forbidden': 'Pet Info access denied.',
    '403-getPaymentCard-Forbidden': 'Payment Card access denied.',
  };

  // ----------
  // #endregion

  // ///////////////////////////

  constructor(private authService: AuthService) {}

  // #region -> Error Generation <-
  // ----------

  /**
   * Try to parse the code info.
   *
   * NOTES:
   * - With the usage of `Apollo` plugin, the error will be packaged as `ApolloError` first.
   * - Within it, the actual error will be saved as `networkError`.
   *  - However, it could be casted as `GraphQLError` actually.
   *
   * @param error
   * @returns
   */
  parseGraphQLError(error: ApolloError | GraphQLError): ParsedGraphQLError {
    // Try to get a "GraphQLError" if needed
    let graphQLError: GraphQLError = undefined;
    if (error instanceof ApolloError) {
      if (error.networkError) {
        graphQLError = error.networkError as GraphQLError;
      }
    } else {
      graphQLError = error;
    }

    if (graphQLError) {
      const newError: ParsedGraphQLError = {
        name: graphQLError.message,
        message: graphQLError.message,
        code: graphQLError.extensions?.code || 'UNKNOWN',
        path: graphQLError.path,
        extensions: graphQLError.extensions,
      };

      // Prepare other info
      return this.buildGraphQLErrorMessages(newError);
    }

    return undefined;
  }

  /**
   * Generate a quick "GraphQL Error" based on some basic info.
   *
   * @param errorCode
   * @param errorMessage
   * @param path
   * @returns
   */
  generateGraphQLError(
    errorName: string,
    errorCode: string,
    errorMessage: string,
    path: string[]
  ): ParsedGraphQLError {
    const newError: ParsedGraphQLError = {
      name: errorName,
      message: errorMessage,
      code: errorCode,
      path: path,
    };

    //
    return this.buildGraphQLErrorMessages(newError);
  }

  /**
   * Build other "messages" based on the basic error info.
   *
   * @param error
   * @returns
   */
  buildGraphQLErrorMessages(error: ParsedGraphQLError): ParsedGraphQLError {
    // Build all "messages"
    error.rewrittenMessage = this.rewriteErrorMessage(error);
    error.outputMessage = this.generateErrorOutputMessage(error);
    error.name = this.generateErrorName(error);
    error.supplementMessage = this.generateErrorSupplementMessage(error);

    return error;
  }

  /**
   * Generate a better error name.
   *
   * Format:
   *  `({error_code}) {error_name}`
   *
   * @param error
   */
  generateErrorName(error: ParsedGraphQLError): string {
    return Array.isArray(error.path) && error.path.length > 0
      ? `(${error.code}) ${error.name} | ${error.path[0]}`
      : `(${error.code}) ${error.name}`;
  }

  /**
   * Generate the "supplement message" to provide a quick glance to the error.
   *
   * Format:
   *  `User({user_id}) -> {path}`
   *
   * @param error
   */
  generateErrorSupplementMessage(error: ParsedGraphQLError): string {
    return Array.isArray(error.path) && error.path.length > 0
      ? `User(${this.authService.currentAccountId}) -> ${error.path[0]}`
      : `User(${this.authService.currentAccountId})`;
  }

  /**
   * Try to re-write the message for a better reading.
   *
   * The re-write is based on a "KNOWN" mapping pool.
   * The `key` is `{code}-{path_1}-{original_message}
   *
   * @param error
   */
  rewriteErrorMessage(error: ParsedGraphQLError): string {
    const mappingKey =
      Array.isArray(error.path) && error.path.length > 0
        ? `${error.code}-${error.path[0]}-${error.message}`
        : `${error.code}-${error.message}`;

    if (mappingKey in this.KNOWN_REWRITE_MESSAGE_PATHS) {
      return this.KNOWN_REWRITE_MESSAGE_PATHS[mappingKey];
    }

    // Use the original error message
    return error.message;
  }

  /**
   * Generate a "human friendly" error message.
   *
   * @param error
   */
  generateErrorOutputMessage(error: ParsedGraphQLError): string {
    // Unknown error message
    if (!error) {
      return 'UNKNOWN_ERROR';
    }

    // Decide the "message" to use
    const message = error.rewrittenMessage || error.message;

    // Full error message
    if (error.path && Array.isArray(error.path)) {
      return `(${error.code}) ${message} | ${error.path[0]}`;
    }

    // Default error rewrittenMessage
    return `(${error.code}) ${message}`;
  }

  // ----------
  // #endregion

  // ///////////////////////////

  // #region -> Error Tracking <-
  // ----------

  reportGraphQLErrorIfNeeded(error, operation: Operation) {
    // Try to build "context" info
    const context: ErrorContextInfo = this._buildErrorContext(error, operation);

    // Report
    this.reportToSentry(error, context);
  }

  /**
   * Manually report a record to Sentry.
   *
   * @param error
   * @param context
   * @param errorLevel Default to `error` level.
   */
  reportToSentry(
    error,
    context: ErrorContextInfo = {},
    errorLevel: Sentry.SeverityLevel = 'error'
  ) {
    // Build the `Error` object since Sentry only accepts it.
    const errorObject = new Error(error.name);
    errorObject.name = error.name;
    errorObject.message = error.rewrittenMessage || error.message;

    Sentry.captureException(errorObject, (scope: Sentry.Scope) => {
      const updatedScope = this._updateSentryScope(scope, context);
      // Error Level
      updatedScope.setLevel(errorLevel);
      return updatedScope;
    });
  }

  /**
   * Build the "error context" based on the given "error" and "operation" info.
   */
  private _buildErrorContext(error, operation: Operation): ErrorContextInfo {
    const context: ErrorContextInfo = {};

    // Try to prepare the "User ID"
    if (
      this.authService.currentAccountId &&
      this.authService.currentAccountEmail
    ) {
      context.user = {
        id: this.authService.currentAccountId,
        username: this.authService.currentAccountEmail,
      };
    }

    // Try to prepare other info.
    if (error.supplementMessage) {
      context.transactionName = error.supplementMessage;
    }
    if (operation) {
      context.tags = {
        ...context.tags,
        operationName: operation.operationName,
        errorCode: error.code,
      };
      context.extra = {
        ...context.extra,
        payload: operation.variables,
        user: context.user,
        tags: context.tags,
        // Also keep the original error info
        originErrorObject: error,
      };
    }

    //
    return context;
  }

  /**
   * Update "scope" based on the passing "context".
   *
   * @param scope
   * @param context
   * @returns
   */
  private _updateSentryScope(
    scope: Sentry.Scope,
    context: ErrorContextInfo
  ): Sentry.Scope {
    // Tags & Extra
    const tags = context.tags || {};
    const extra = context.extra || {};
    scope.setTags(tags);
    scope.setExtras(extra);

    // Title
    if (context.transactionName) {
      scope.setTransactionName(context.transactionName);
    }

    // User
    if (context.user) {
      scope.setUser(context.user);
    }

    // console.log('Sentry Error Scope: ', scope);

    return scope;
  }

  // ----------
  // #endregion

  // ///////////////////////////
}
